import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DocumentActions from '~/actions/document_actions';
import DocumentCategorySelect from '~/components/forms/dropdowns/DocumentCategorySelect';
import DocumentStatusSelect from '~/components/forms/dropdowns/DocumentStatusSelect';
import { TeammatePicker } from '~/components/forms/dropdowns/TeamMemberSelect';
import DocumentStore from '~/stores/document_store';
import AsyncSelect from '~/components/forms/dropdowns/SelectWrapper/AsyncSelect';
import AppModal from './app_modal';

const EditDocumentModal = ({
  documentID, containerID, leadID, modalClass = '', dialogClass = '', onClose, table, query,
}) => {
  const [loadingDocument, setLoadingDocument] = useState(!!documentID);
  const [document, setDocument] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const documentStoreListener = DocumentStore.addListener(() => {
      const documentState = DocumentStore.getState();
      const {
        document, errors, lastDocumentStoreAction,
      } = documentState;

      if (lastDocumentStoreAction === 'updateDocumentDone') {
        setSubmitting(false);
        if (onClose) onClose();
      } else if (lastDocumentStoreAction === 'updateDocumentFail') {
        console.error('Failed to update document:', errors);
        setErrors(errors);
        setSubmitting(false);
      } else if (lastDocumentStoreAction === 'loadDocumentDone') {
        setLoadingDocument(false);
        setDocument(document);
      }
    });

    if (documentID) {
      DocumentActions.loadDocument(documentID);
    }

    return () => {
      documentStoreListener.remove();
    };
  }, [documentID, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      DocumentActions.updateDocument(document, leadID, table, query);
    } else {
      setErrors(validationErrors);
      setSubmitting(false);
    }
  };

  const setDocumentField = (name, val) => {
    setDocument((prevDocument) => ({
      ...prevDocument,
      [name]: val,
    }));
  };

  const validate = () => {
    const validationErrors = {};
    if (!document?.title) {
      validationErrors.title = "Can't be empty";
    }
    setErrors(validationErrors);
    return validationErrors;
  };

  const renderDocumentForm = () => {
    if (loadingDocument || !document) {
      return (
        <div className="text-center">
          <FontAwesomeIcon icon="far fa-spinner" pulse size="lg" />
        </div>
      );
    }

    const status_id =
      'status_id' in document
        ? document.status_id || ''
        : document.status?.id || '';
    const category_id =
      'category_id' in document
        ? document.category_id || ''
        : document.category?.id || '';

    return (
      <>
        <div className="form-group mb-3">
          <label htmlFor="document_title">File name</label>
          <input
            type="text"
            value={document.title || ''}
            id="document_title"
            name="title"
            className={classNames('form-control', { 'is-invalid': errors.title })}
            placeholder="Enter Name"
            onChange={(e) => setDocumentField('title', e.target.value)}
          />
          {errors.title && <div className="invalid-feedback">{errors.title}</div>}
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="document_status">Status</label>
            <DocumentStatusSelect
              id="document_status"
              value={status_id}
              onChange={(opt) => setDocumentField('status_id', opt?.value || null)}
              isInvalid={!!errors.status_id}
            />
            {errors.status_id && <div className="invalid-feedback d-block">{errors.status_id}</div>}
          </div>

          <div className="form-group col-md-6">
            <label htmlFor="document_category">Category</label>
            <DocumentCategorySelect
              id="document_category"
              value={category_id}
              onChange={(opt) => setDocumentField('category_id', opt?.value || null)}
              isInvalid={!!errors.category_id}
            />
            {errors.category_id && <div className="invalid-feedback d-block">{errors.category_id}</div>}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="document_owner">Owner</label>
          <TeammatePicker
            id="document_owner"
            name="owner"
            placeholder="Enter Owner"
            value={document.owner_id || ''}
            onChange={(opt) => setDocumentField('owner_id', opt?.value || null)}
            isInvalid={!!errors.owner}
            apiResource="/documents/owner_options"
            isClearable
          />
          {errors.owner && <div className="invalid-feedback d-block">{errors.owner}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="document-related-contacts">Related Contacts</label>
          <AsyncSelect
            id="document-related-contacts"
            placeholder="Please Select Contacts ..."
            resourcePath="leads"
            formatOption={(lead) => ({
              value: lead.id,
              label: lead.name,
            })}
            isMulti
            isSearchable
            value={document.related_contacts.related_contact_ids || []}
            onChange={(values) => {
              setDocumentField('related_contacts', {
                related_contact_ids: (values ? values.map((item) => item.value) : []),
              });
            }}
          />
        </div>
      </>
    );
  };

  return (
    <AppModal
      containerID={containerID}
      modalClass={modalClass}
      dialogClass={dialogClass}
    >
      <form
        method="PUT"
        action={`/api/v1/documents/${document?.id || ''}`}
        onSubmit={handleSubmit}
      >
        <div className="modal-body">
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: '10px', right: '10px', zIndex: 9999,
            }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          {renderDocumentForm()}
        </div>

        <div className="modal-footer">
          {submitting ? (
            <button type="submit" className="btn btn-success" disabled>
              <FontAwesomeIcon icon="spinner" pulse className="mr-2" />
              Updating ...
            </button>
          ) : (
            <button type="submit" className="btn btn-success">
              Confirm
            </button>
          )}
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Close
          </button>
        </div>
      </form>
    </AppModal>
  );
};

EditDocumentModal.propTypes = {
  documentID:  PropTypes.number.isRequired,
  containerID: PropTypes.string.isRequired,
  leadID:      PropTypes.number,
  modalClass:  PropTypes.string,
  dialogClass: PropTypes.string,
  onClose:     PropTypes.func.isRequired,
  table:       PropTypes.object,
  query:       PropTypes.object,
};

export default EditDocumentModal;
